import { Icon, Tooltip, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { observer } from 'mobx-react-lite'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { iconMapping } from 'src/util/icon-mapping'

const DEFAULT_ICON_BACKGROUND = 'error.light'
const DEFAULT_ICON_COLOR = 'error.main'

type InsightMetric = {
  id: string
  label: string
  value: number | string
  subtitle?: string
  icon?: keyof typeof iconMapping
  iconColor?: string
  iconBackground?: string
  toolTipDesc?: string
}

export type InsightMetricsProps = {
  sx: SxProps<Theme>
  metrics: InsightMetric[]
  isLoading: boolean
}

const getIconChipStyles = (backgroundColor?: string) => ({
  ...styles.iconChip,
  backgroundColor: backgroundColor || DEFAULT_ICON_BACKGROUND,
})

const getMetricIconStyles = (color?: string) => ({
  color: color || DEFAULT_ICON_COLOR,
  fontSize: 14,
})

export const InsightMetrics = observer(({ sx, metrics, isLoading }: InsightMetricsProps) => {
  return (
    <AppBox sx={sx}>
      {metrics.map(metric => (
        <AppPaper key={metric.id} sx={styles.card}>
          <AppBox sx={styles.topRow}>
            <AppBox sx={styles.labelRow}>
              {metric.toolTipDesc && (
                <Tooltip title={metric.toolTipDesc} placement='top' arrow>
                  <Icon sx={styles.helpIcon} component={iconMapping['InfoIcon']} />
                </Tooltip>
              )}
              <Tooltip title={metric.label} placement='top' arrow>
                <Typography sx={styles.label} noWrap>
                  {metric.label}
                </Typography>
              </Tooltip>
            </AppBox>

            {metric.icon && (
              <AppBox sx={getIconChipStyles(metric.iconBackground)}>
                <Icon
                  component={iconMapping[metric.icon]}
                  sx={getMetricIconStyles(metric.iconColor)}
                />
              </AppBox>
            )}
          </AppBox>

          {!isLoading ? (
            <Tooltip title={String(metric.value)} placement='top' arrow>
              <Typography variant='h4' sx={styles.value}>
                {metric.value}
              </Typography>
            </Tooltip>
          ) : (
            <AppSkeleton count={1} direction='column' rects={[{ width: '70%', height: 30 }]} />
          )}

          {metric.subtitle && (
            <Typography sx={styles.subtitle}>
              {metric.subtitle}
            </Typography>
          )}
        </AppPaper>
      ))}
    </AppBox>
  )
})

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '10px',
    border: 0,
    padding: '12px',
    gap: 5,
    height: '100%',
    minWidth: 0,
    backgroundColor: 'background.paper',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
    width: '100%',
    minWidth: 0,
  },
  label: {
    color: 'text.secondary',
    fontSize: 11,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 5,
    flex: 1,
    minWidth: 0,
  },
  helpIcon: {
    height: 15,
    width: 15,
    color: 'secondary.main',
    cursor: 'help',
    flexShrink: 0,
  },
  iconChip: {
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
  },
  value: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    color: 'text.secondary',
    fontSize: 10,
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
    marginTop: 'auto',
  },
}
